import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Clock3, FilePlus2, History, Save, ShieldCheck } from "lucide-react";
import { API_URL } from "../config";
import { apiRequest, formatApiError } from "../apiRequest";
import KpiScoringRuleEditor from "../components/KpiScoringRuleEditor";
import KpiPageHeader from "../components/KpiPageHeader";

function tokenHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
}

async function kpiRequest(path, method = "GET", body) {
  const { data } = await apiRequest(`${API_URL}${path}`, {
    method,
    headers: { ...tokenHeaders(), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  }, { retries: 0 });
  return data;
}

function newRow(metric) {
  return {
    metric_key: metric.metric_key,
    display_name: metric.display_name,
    retired_at: metric.retired_at,
    is_applicable: false,
    is_active: false,
    weight_percent: 0,
    scoring_rule: null,
    target_config: null,
    maximum_score: null,
    aggregation_method: null,
    missing_data_policy: null
  };
}

function mergeRows(metrics, saved = []) {
  const byKey = new Map(saved.map((item) => [item.metric_key, item]));
  return metrics.map((metric) => {
    const item = byKey.get(metric.metric_key);
    return item ? {
      ...newRow(metric),
      ...item,
      is_applicable: Boolean(item.is_applicable),
      is_active: Boolean(item.is_active)
    } : newRow(metric);
  });
}

function effectiveLabel(version) {
  if (!version) return "No version selected";
  const end = version.effective_to ? new Date(`${version.effective_to}T00:00:00`).toLocaleDateString() : "Current";
  return `${version.effective_from || "Not set"} to ${end}`;
}

const APPROVED_CATALOG = [
  ["discipline", "Discipline"], ["late_login", "Late Login"], ["unplanned_leave", "Unplanned Leave"],
  ["hubspot", "HubSpot"], ["mpe", "MPE"], ["call_quality", "Call Quality"],
  ["dsr", "DSR KPI"], ["am_tl_fs_feedback", "TL + AM Feedback"]
];

export default function KpiConfigurationPage({ onBack, onHome, onNavigate, canBootstrapCatalog = false, canApplyQuarterlyTemplate = false }) {
  const [catalog, setCatalog] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [designation, setDesignation] = useState("");
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(null);
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [quarterlyMethod, setQuarterlyMethod] = useState("");
  const [quarterWeights, setQuarterWeights] = useState([1, 1, 1]);
  const [gradeBands, setGradeBands] = useState([]);
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [expandedMetric, setExpandedMetric] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [bootstrapConfirmation, setBootstrapConfirmation] = useState("");
  const [bootstrapReason, setBootstrapReason] = useState("");
  const [quarterlyInventory, setQuarterlyInventory] = useState(null);
  const [applyTemplatePreview, setApplyTemplatePreview] = useState(false);
  const [applyTemplateReason, setApplyTemplateReason] = useState("");

  const loadVersion = useCallback(async (id, metrics = catalog) => {
    const detail = await kpiRequest(`/kpi-config/versions/${encodeURIComponent(id)}`);
    setVersion(detail);
    setRows(mergeRows(metrics, detail.items || []));
    setEffectiveFrom(detail.effective_from || "");
    setEffectiveTo(detail.effective_to || "");
    setQuarterlyMethod(detail.quarterly_aggregation_method || "");
    const quarterConfig = detail.quarterly_aggregation_config_json
      ? (typeof detail.quarterly_aggregation_config_json === "string" ? JSON.parse(detail.quarterly_aggregation_config_json) : detail.quarterly_aggregation_config_json)
      : {};
    setQuarterWeights(Array.isArray(quarterConfig.monthWeights) ? quarterConfig.monthWeights : [1, 1, 1]);
    setGradeBands((detail.gradeBands || []).map((band) => ({ ...band, is_active: Boolean(band.is_active) })));
    setReason("");
    setValidation(null);
    setConfirmPublish(false);
  }, [catalog]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    kpiRequest("/kpi-config/metrics").then((data) => {
      if (!active) return;
      setCatalog(data.metrics || []);
      setDesignations(data.eligibleDesignations || []);
      setDesignation((current) => current || data.eligibleDesignations?.[0] || "");
      setRows(mergeRows(data.metrics || []));
      setError("");
    }).catch((requestError) => {
      if (active) setError(formatApiError(requestError, "KPI configuration could not be loaded."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!canApplyQuarterlyTemplate) return undefined;
    let active = true;
    kpiRequest("/kpi-config/quarterly-inventory").then((data) => {
      if (active) setQuarterlyInventory(data);
    }).catch(() => {
      if (active) setQuarterlyInventory({ error: true });
    });
    return () => { active = false; };
  }, [canApplyQuarterlyTemplate]);

  useEffect(() => {
    if (!designation || !catalog.length) {
      setVersions([]);
      setVersion(null);
      setRows(mergeRows(catalog));
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    kpiRequest(`/kpi-config/versions?designation=${encodeURIComponent(designation)}`).then(async (data) => {
      if (!active) return;
      const listed = data.versions || [];
      setVersions(listed);
      const initial = listed.find((item) => item.status === "draft") || listed[0];
      if (initial) await loadVersion(initial.id, catalog);
      else {
        setVersion(null);
        setRows(mergeRows(catalog));
        setEffectiveFrom("");
        setEffectiveTo("");
      }
    }).catch((requestError) => {
      if (active) setError(formatApiError(requestError, "Configuration history could not be loaded."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [designation, catalog, loadVersion]);

  useEffect(() => {
    if (!version?.id) { setAudit([]); return undefined; }
    let active = true;
    kpiRequest(`/kpi-config/versions/${encodeURIComponent(version.id)}/audit`).then((data) => {
      if (active) setAudit(data.events || []);
    }).catch(() => { if (active) setAudit([]); });
    return () => { active = false; };
  }, [version?.id]);

  const totalWeight = useMemo(() => rows.reduce((total, item) => (
    item.is_applicable && item.is_active ? total + (Number(item.weight_percent) || 0) : total
  ), 0), [rows]);
  const totalValid = Math.abs(totalWeight - 100) < 1e-8;
  const readOnly = Boolean(version && version.status !== "draft");
  const canEdit = Boolean(version && version.status === "draft");
  const isEmptyDraft = Boolean(canApplyQuarterlyTemplate && canEdit && !effectiveFrom && !effectiveTo
    && !quarterlyMethod && !version?.quarterly_aggregation_config_json && gradeBands.length === 0
    && rows.every((item) => !item.is_applicable && !item.is_active
      && (item.weight_percent == null || Number(item.weight_percent) === 0)
      && (item.maximum_score == null || Number(item.maximum_score) === 0)
      && !item.target_config && !item.scoring_rule && !item.aggregation_method && !item.missing_data_policy));
  const effectiveDate = new Date(`${effectiveFrom}T00:00:00`);
  const midMonth = Boolean(effectiveFrom && Number.isFinite(effectiveDate.getTime()) && effectiveDate.getDate() !== 1);

  const setItem = (key, update) => {
    setRows((current) => current.map((item) => item.metric_key === key ? { ...item, ...update } : item));
    setValidation(null);
    setConfirmPublish(false);
  };

  const refreshVersions = async (selectedId = version?.id) => {
    const data = await kpiRequest(`/kpi-config/versions?designation=${encodeURIComponent(designation)}`);
    setVersions(data.versions || []);
    if (selectedId) await loadVersion(selectedId, catalog);
  };

  const createDraft = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const source = versions.find((entry) => entry.status === "published");
      const draft = await kpiRequest("/kpi-config/drafts", "POST", { designation, sourceVersionId: source?.id || null });
      await refreshVersions(draft.id);
      setNotice(`Draft v${draft.version_number} created.`);
    } catch (requestError) {
      setError(formatApiError(requestError, "Draft could not be created."));
    } finally { setBusy(false); }
  };

  const draftPayload = () => ({
    expectedRevision: version?.revision,
    effectiveFrom: effectiveFrom || null,
    effectiveTo: effectiveTo || null,
    quarterlyAggregationMethod: quarterlyMethod || null,
    quarterlyAggregationConfig: quarterlyMethod === "weighted_monthly" ? { monthWeights: quarterWeights.map(Number) } : quarterlyMethod === "quarterly_category_policy" ? { policyVersion: "quarterly-v1" } : null,
    gradeBands: gradeBands.map((band, display_order) => ({ ...band, minimum_score: Number(band.minimum_score), maximum_score: Number(band.maximum_score), display_order })),
    reason: reason || null,
    items: rows.map(({ metric_key, is_applicable, is_active, weight_percent, target_config, scoring_rule, maximum_score, aggregation_method, missing_data_policy }) => ({
      metric_key, is_applicable, is_active,
      weight_percent: weight_percent === "" ? null : Number(weight_percent),
      target_config, scoring_rule, maximum_score,
      aggregation_method: aggregation_method || null,
      missing_data_policy: missing_data_policy || null
    }))
  });

  const saveDraft = async (quiet = false) => {
    if (!canEdit) return false;
    if (gradeBands.some((band) => !String(band.label || "").trim() || band.minimum_score === "" || band.maximum_score === "")) {
      throw new Error("Complete each performance band label and score range before saving.");
    }
    const saved = await kpiRequest(`/kpi-config/drafts/${encodeURIComponent(version.id)}`, "PUT", draftPayload());
    setVersion(saved);
    setRows(mergeRows(catalog, saved.items || []));
    if (!quiet) setNotice("Draft saved.");
    setValidation(null);
    return true;
  };

  const runValidation = async () => {
    if (!canEdit) return;
    setBusy(true); setError(""); setNotice(""); setConfirmPublish(false);
    try {
      await saveDraft(true);
      const outcome = await kpiRequest(`/kpi-config/drafts/${encodeURIComponent(version.id)}/validate`, "POST", {});
      setValidation(outcome);
      setNotice(outcome.valid ? "Configuration is valid for publishing." : "Configuration needs changes before publishing.");
    } catch (requestError) {
      setError(formatApiError(requestError, "Configuration could not be validated."));
    } finally { setBusy(false); }
  };

  const beginPublish = async () => {
    if (!canEdit) return;
    setBusy(true); setError(""); setNotice("");
    try {
      if (!reason.trim()) throw new Error("Enter a change reason before publishing.");
      await saveDraft(true);
      const outcome = await kpiRequest(`/kpi-config/drafts/${encodeURIComponent(version.id)}/validate`, "POST", {});
      setValidation(outcome);
      if (!outcome.valid) {
        setNotice("Configuration needs changes before publishing.");
        return;
      }
      setConfirmPublish(true);
    } catch (requestError) {
      setError(formatApiError(requestError, "Configuration could not be prepared for publishing."));
    } finally { setBusy(false); }
  };

  const publishDraft = async () => {
    setBusy(true); setError(""); setConfirmPublish(false);
    try {
      const published = await kpiRequest(`/kpi-config/drafts/${encodeURIComponent(version.id)}/publish`, "POST", { reason: reason.trim() });
      setVersion(published);
      await refreshVersions(published.id);
      setNotice(`Version ${published.version_number} published.`);
    } catch (requestError) {
      setError(formatApiError(requestError, "Configuration could not be published."));
    } finally { setBusy(false); }
  };

  const bootstrapCatalog = async (event) => {
    event.preventDefault();
    if (!canBootstrapCatalog || bootstrapConfirmation !== "BOOTSTRAP_QUARTERLY_KPI_CATALOG" || !bootstrapReason.trim()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const outcome = await kpiRequest("/kpi-quarterly/catalog-bootstrap", "POST", {
        confirmation: bootstrapConfirmation,
        reason: bootstrapReason.trim()
      });
      const data = await kpiRequest("/kpi-config/metrics");
      const approvedKeys = new Set(APPROVED_CATALOG.map(([metricKey]) => metricKey));
      const actual = (data.metrics || []).filter((metric) => approvedKeys.has(metric.metric_key) && !metric.retired_at)
        .map(({ metric_key, display_name }) => [metric_key, display_name]).sort(([a], [b]) => a.localeCompare(b));
      const expected = [...APPROVED_CATALOG].sort(([a], [b]) => a.localeCompare(b));
      if (JSON.stringify(actual) !== JSON.stringify(expected) || outcome.verifiedMetrics !== 8) {
        throw new Error("Bootstrap completed, but catalog read-back did not match all eight approved KPI definitions. Stop and ask an administrator to inspect the catalog before retrying.");
      }
      setCatalog(data.metrics || []);
      setDesignations(data.eligibleDesignations || []);
      setRows(mergeRows(data.metrics || []));
      setDesignation((current) => current || data.eligibleDesignations?.[0] || "");
      setBootstrapConfirmation("");
      setBootstrapReason("");
      setNotice(outcome.replayed ? "Existing KPI catalog verified. No definitions were added." : "Eight approved KPI definitions were created and verified. Create and review the Q4 policy drafts before any entries are enabled.");
    } catch {
      setError("KPI catalog bootstrap could not be verified. Check the catalog status before retrying.");
    } finally { setBusy(false); }
  };

  const applyApprovedTemplate = async () => {
    if (!canApplyQuarterlyTemplate || !isEmptyDraft || !applyTemplateReason.trim()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await kpiRequest(`/kpi-config/drafts/${encodeURIComponent(version.id)}/apply-quarterly-template`, "POST", {
        expectedRevision: version.revision,
        expectedDesignation: version.designation,
        expectedVersionNumber: version.version_number,
        reason: applyTemplateReason.trim()
      });
      await loadVersion(result.draft.id, catalog);
      const [inventory, auditData] = await Promise.all([
        kpiRequest("/kpi-config/quarterly-inventory"),
        kpiRequest(`/kpi-config/versions/${encodeURIComponent(result.draft.id)}/audit`)
      ]);
      setQuarterlyInventory(inventory);
      setAudit(auditData.events || []);
      setApplyTemplatePreview(false);
      setApplyTemplateReason("");
      setNotice(result.replayed ? "This draft already has the approved quarterly template. No new version was created." : "Approved quarterly template applied to this existing draft. It remains unpublished.");
    } catch (requestError) {
      setError(formatApiError(requestError, "The approved template was not applied. Reload the draft and review its configuration."));
      await refreshVersions(version?.id).catch(() => {});
    } finally { setBusy(false); }
  };

  return (
    <main className="kpi-module-page kpi-config-page">
      <KpiPageHeader title="KPI Configuration" subtitle="Configuration versions & effective periods" icon={ShieldCheck} onBack={onHome || onBack} backLabel={onHome ? "Home" : "KPI Dashboard"} activeView="config" onNavigate={onNavigate}>
        <label className="kpi-designation-select"><span>Designation</span><select aria-label="Designation" value={designation} onChange={(event) => setDesignation(event.target.value)} disabled={loading}>{designations.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
      </KpiPageHeader>

      {error && <div className="kpi-config-message error" role="alert">{error}</div>}
      {notice && <div className="kpi-config-message success" role="status">{notice}</div>}
      {loading && <p className="kpi-config-loading">Loading KPI configuration…</p>}

      {!loading && catalog.length === 0 && (
        <section className="kpi-config-catalog-empty">
          <h2>KPI definitions are not initialized</h2>
          <p>No KPI definitions are available. Configuration versions will appear here when definitions have been added.</p>
          {canBootstrapCatalog && <form aria-label="KPI catalog bootstrap" onSubmit={bootstrapCatalog}>
            <label className="kpi-config-field"><span>Audit reason</span><textarea aria-label="Bootstrap audit reason" rows="3" maxLength="2000" required value={bootstrapReason} onChange={(event) => setBootstrapReason(event.target.value)} /></label>
            <label className="kpi-config-field"><span>Type BOOTSTRAP_QUARTERLY_KPI_CATALOG to confirm</span><input aria-label="Bootstrap confirmation" value={bootstrapConfirmation} onChange={(event) => setBootstrapConfirmation(event.target.value)} autoComplete="off" /></label>
            <button className="kpi-primary-button" type="submit" disabled={busy || bootstrapConfirmation !== "BOOTSTRAP_QUARTERLY_KPI_CATALOG" || !bootstrapReason.trim()}><ShieldCheck size={16} aria-hidden="true" />{busy ? "Verifying catalog..." : "Bootstrap approved KPI catalog"}</button>
          </form>}
        </section>
      )}

      {!loading && catalog.length > 0 && (
        <div className="kpi-config-layout">
          <section className="kpi-config-main">
            <div className="kpi-config-versionbar">
              <div><span className="kpi-config-label">Selected version</span><strong>{version ? `v${version.version_number}` : "No configuration"}</strong><span className={`kpi-version-status ${version?.status || "empty"}`}>{version?.status || "Not configured"}</span></div>
              <div><span className="kpi-config-label">Effective window</span><strong>{effectiveLabel(version)}</strong></div>
              <button className="kpi-secondary-button" type="button" disabled={busy} onClick={createDraft}><FilePlus2 size={16} aria-hidden="true" /> New draft</button>
            </div>

            {isEmptyDraft && <section className="kpi-quarterly-config" aria-labelledby="apply-quarterly-template-title">
              <h2 id="apply-quarterly-template-title">Approved quarterly template</h2>
              <p>Apply the approved eight-category rules to this empty draft without creating another version. This action does not publish the policy.</p>
              {!applyTemplatePreview ? <button type="button" className="kpi-secondary-button" disabled={busy || !quarterlyInventory?.catalogReady || !quarterlyInventory?.template?.length} onClick={() => setApplyTemplatePreview(true)}>Preview approved template</button> : <div className="kpi-publish-confirm" aria-label="Approved template preview">
                <h3>Effective 2026-10-01 · No end date</h3>
                <ol>{(quarterlyInventory?.template || []).map((item) => <li key={item.metric_key}><details><summary>{item.display_name}: {item.weight_percent} points</summary><pre>{JSON.stringify(item.scoring_rule, null, 2)}</pre></details></li>)}</ol>
                <p>Quarterly aggregation; missing required evidence blocks finalization. No incidents automatically earns full points for Discipline, Late Login and UPL.</p>
                <label className="kpi-config-field"><span>Audit reason</span><input aria-label="Template application audit reason" value={applyTemplateReason} maxLength={500} onChange={(event) => setApplyTemplateReason(event.target.value)} placeholder="Why is the approved template being applied?" /></label>
                <button type="button" className="kpi-primary-button" disabled={busy || !applyTemplateReason.trim()} onClick={applyApprovedTemplate}>Confirm apply to v{version.version_number}</button>
                <button type="button" className="kpi-secondary-button" disabled={busy} onClick={() => setApplyTemplatePreview(false)}>Cancel</button>
              </div>}
            </section>}

            {!version && <div className="kpi-config-table-wrap kpi-config-catalog-table"><table className="kpi-config-table"><thead><tr><th>KPI</th><th>Applicable</th><th>Active</th><th>Weight</th><th>Scoring</th><th>Aggregation</th><th>Missing data</th><th>Status</th></tr></thead><tbody>{catalog.map((metric) => <tr key={metric.metric_key}><th scope="row">{metric.display_name}</th><td>—</td><td>—</td><td>—</td><td>Not set</td><td>—</td><td>—</td><td>No configuration</td></tr>)}</tbody></table></div>}

            {version && <>
              <div className="kpi-config-dates">
                <label className="kpi-config-field"><span>Effective from</span><input type="date" value={effectiveFrom} disabled={!canEdit} onChange={(event) => { setEffectiveFrom(event.target.value); setValidation(null); }} /></label>
                <label className="kpi-config-field"><span>Optional end date</span><input type="date" value={effectiveTo} min={effectiveFrom || undefined} disabled={!canEdit} onChange={(event) => { setEffectiveTo(event.target.value); setValidation(null); }} /></label>
                {midMonth && <p className="kpi-config-warning"><Clock3 size={16} aria-hidden="true" /> This configuration becomes effective during a reporting month. Monthly results may contain multiple configuration segments.</p>}
              </div>
              <section className="kpi-quarterly-config" aria-labelledby="kpi-quarterly-config-title">
                <h2 id="kpi-quarterly-config-title">Quarterly calculation</h2>
                <label className="kpi-config-field"><span>Aggregation method</span><select value={quarterlyMethod} disabled={!canEdit} onChange={(event) => { setQuarterlyMethod(event.target.value); setValidation(null); }}><option value="">Not configured</option><option value="average_monthly">Average monthly results (historical)</option><option value="weighted_monthly">Weighted monthly results (historical)</option><option value="quarterly_category_policy">Quarterly category policy</option></select></label>
                {quarterlyMethod === "weighted_monthly" && <div className="kpi-quarter-weight-fields">{["First month", "Second month", "Third month"].map((label, index) => <label className="kpi-config-field" key={label}><span>{label} weight</span><input type="number" min="0.01" step="0.01" value={quarterWeights[index]} disabled={!canEdit} onChange={(event) => setQuarterWeights((current) => current.map((value, position) => position === index ? event.target.value : value))} /></label>)}</div>}
                <p>{quarterlyMethod === "quarterly_category_policy" ? "Official quarterly points use incident, monthly and quarterly evidence. Missing required evidence blocks finalization. Daily/monthly values are collection progress." : "Historical monthly calculations are retained for compatibility. They are not authoritative finalized quarterly outcomes."}</p>
              </section>
              <section className="kpi-quarterly-config" aria-labelledby="kpi-grade-config-title">
                <header className="kpi-grade-heading"><h2 id="kpi-grade-config-title">Performance bands</h2><button className="kpi-secondary-button" type="button" disabled={!canEdit} onClick={() => setGradeBands((current) => [...current, { band_key: `band-${Date.now()}-${current.length}`, label: "", minimum_score: 0, maximum_score: 0, is_active: true, display_order: current.length }])}>Add band</button></header>
                <p>Optional display labels classify calculated scores only. No bands are prefilled. Active ranges cannot overlap; scores outside configured ranges remain unclassified.</p>
                {gradeBands.length > 0 && <div className="kpi-grade-list">{gradeBands.map((band, index) => <div className="kpi-grade-row" key={band.band_key}>
                  <label className="kpi-config-field"><span>Label</span><input aria-label={`Band ${index + 1} label`} value={band.label} maxLength={80} disabled={!canEdit} onChange={(event) => setGradeBands((current) => current.map((item, position) => position === index ? { ...item, label: event.target.value } : item))} /></label>
                  <label className="kpi-config-field"><span>Minimum score</span><input aria-label={`Band ${index + 1} minimum`} type="number" min="0" max="1000000" step="0.01" value={band.minimum_score} disabled={!canEdit} onChange={(event) => setGradeBands((current) => current.map((item, position) => position === index ? { ...item, minimum_score: event.target.value } : item))} /></label>
                  <label className="kpi-config-field"><span>Maximum score</span><input aria-label={`Band ${index + 1} maximum`} type="number" min="0" max="1000000" step="0.01" value={band.maximum_score} disabled={!canEdit} onChange={(event) => setGradeBands((current) => current.map((item, position) => position === index ? { ...item, maximum_score: event.target.value } : item))} /></label>
                  <label className="kpi-grade-active"><input type="checkbox" checked={band.is_active} disabled={!canEdit} onChange={(event) => setGradeBands((current) => current.map((item, position) => position === index ? { ...item, is_active: event.target.checked } : item))} /> Active</label>
                  <button type="button" className="kpi-settings-button" disabled={!canEdit} aria-label={`Remove ${band.label || `band ${index + 1}`}`} onClick={() => setGradeBands((current) => current.filter((_, position) => position !== index))}>Remove</button>
                </div>)}</div>}
              </section>

              <div className="kpi-weight-summary" data-valid={totalValid}>
                <span>Total active applicable weight</span><strong>{totalWeight.toFixed(2)}%</strong><span>{totalValid ? "Valid" : "Must equal 100% to publish"}</span>
              </div>

              <div className="kpi-config-table-wrap">
                <table className="kpi-config-table">
                  <thead><tr><th>KPI</th><th>Applicable</th><th>Active</th><th>Weight</th><th>Scoring</th><th>Aggregation</th><th>Missing data</th><th>Settings</th></tr></thead>
                  <tbody>{rows.map((item) => {
                    const expanded = item.metric_key === expandedMetric;
                    return <React.Fragment key={item.metric_key}>
                      <tr className={item.retired_at ? "retired" : ""}>
                        <th scope="row">{item.display_name}{item.retired_at && <small>Retired</small>}</th>
                        <td><input type="checkbox" aria-label={`${item.display_name} applicable`} checked={item.is_applicable} disabled={!canEdit || Boolean(item.retired_at)} onChange={(event) => setItem(item.metric_key, { is_applicable: event.target.checked, is_active: event.target.checked ? item.is_active : false })} /></td>
                        <td><input type="checkbox" aria-label={`${item.display_name} active`} checked={item.is_active} disabled={!canEdit || !item.is_applicable || Boolean(item.retired_at)} onChange={(event) => setItem(item.metric_key, { is_active: event.target.checked })} /></td>
                        <td><input className="kpi-weight-input" type="number" min="0" max="100" step="0.1" aria-label={`${item.display_name} weight`} value={item.weight_percent ?? ""} disabled={!canEdit || !item.is_applicable || !item.is_active} onChange={(event) => setItem(item.metric_key, { weight_percent: event.target.value === "" ? "" : Number(event.target.value) })} /></td>
                        <td>{item.scoring_rule?.type?.replaceAll("_", " ") || "Not set"}</td>
                        <td><select aria-label={`${item.display_name} aggregation`} value={item.aggregation_method || ""} disabled={!canEdit || !item.is_applicable || !item.is_active} onChange={(event) => setItem(item.metric_key, { aggregation_method: event.target.value || null })}><option value="">Choose</option><option value="average">Average</option><option value="sum">Sum</option><option value="count">Count</option><option value="ratio">Ratio</option><option value="target_actual">Target vs actual</option><option value="end_of_period">End of period</option></select></td>
                        <td><select aria-label={`${item.display_name} missing-data policy`} value={item.missing_data_policy || ""} disabled={!canEdit || !item.is_applicable || !item.is_active} onChange={(event) => setItem(item.metric_key, { missing_data_policy: event.target.value || null })}><option value="">Choose</option><option value="exclude">Exclude</option><option value="zero">Use zero</option><option value="block">Block</option></select></td>
                        <td><button type="button" className="kpi-settings-button" aria-expanded={expanded} onClick={() => setExpandedMetric(expanded ? "" : item.metric_key)}>{expanded ? "Close" : "Configure"}</button></td>
                      </tr>
                      {expanded && <tr className="kpi-config-settings-row"><td colSpan="8"><div className="kpi-config-settings-panel">
                        <KpiScoringRuleEditor rule={item.scoring_rule} onChange={(scoring_rule) => setItem(item.metric_key, { scoring_rule })} maximumScore={item.maximum_score} onMaximumScore={(maximum_score) => setItem(item.metric_key, { maximum_score })} targetConfig={item.target_config} onTargetConfig={(target_config) => setItem(item.metric_key, { target_config })} disabled={!canEdit || !item.is_applicable || !item.is_active} />
                      </div></td></tr>}
                    </React.Fragment>;
                  })}</tbody>
                </table>
              </div>

              <div className="kpi-config-actions">
                {canEdit && <>
                  <button type="button" className="kpi-secondary-button" disabled={busy} onClick={() => { setBusy(true); setError(""); saveDraft().catch((requestError) => setError(formatApiError(requestError, "Draft could not be saved."))).finally(() => setBusy(false)); }}><Save size={16} aria-hidden="true" /> Save draft</button>
                  <button type="button" className="kpi-secondary-button" disabled={busy} onClick={runValidation}><Check size={16} aria-hidden="true" /> Validate configuration</button>
                  <label className="kpi-config-field kpi-reason-field"><span>Change reason</span><input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="Required to publish" /></label>
                  <button type="button" className="kpi-primary-button" disabled={busy || !reason.trim()} onClick={beginPublish}>Publish version</button>
                </>}
                {readOnly && <span className="kpi-readonly-note">Published versions are read-only.</span>}
              </div>

              {validation && <section className={`kpi-validation-result ${validation.valid ? "valid" : "invalid"}`} role="status"><h2>{validation.valid ? "Configuration is valid" : "Configuration needs attention"}</h2>{validation.errors?.length > 0 && <ul>{validation.errors.map((message, index) => <li key={`${index}-${message}`}>{message}</li>)}</ul>}</section>}
              {confirmPublish && <section className="kpi-publish-confirm" aria-label="Publish confirmation"><div><h2>Publish KPI configuration?</h2><p><b>Designation:</b> {designation} <b>Version:</b> v{version.version_number} <b>Effective:</b> {effectiveFrom} <b>Weight:</b> {totalWeight.toFixed(2)}%</p><p><b>Reason:</b> {reason}</p><p>This version becomes immutable once published.</p></div><button type="button" className="kpi-primary-button" disabled={busy} onClick={publishDraft}>Confirm publish</button><button type="button" className="kpi-secondary-button" disabled={busy} onClick={() => setConfirmPublish(false)}>Cancel</button></section>}
            </>}
          </section>

          <aside className="kpi-config-history">
            {canApplyQuarterlyTemplate && <section aria-labelledby="quarterly-policy-inventory-title"><h2 id="quarterly-policy-inventory-title">Quarterly policy inventory</h2>
              {!quarterlyInventory && <p>Loading policy inventory…</p>}
              {quarterlyInventory?.error && <p role="status">Policy inventory is unavailable. No policies were changed.</p>}
              {quarterlyInventory && !quarterlyInventory.error && <>
                <p>Approved catalog: {quarterlyInventory.catalogReady ? "Verified" : "Incomplete or incompatible"} · Bootstrap audit: {quarterlyInventory.bootstrapAudit ? "Present" : "Not recorded"}</p>
                {quarterlyInventory.designations.map((entry) => <div key={entry.designation} className="kpi-inventory-designation"><strong>{entry.designation}</strong>{entry.versions.length === 0 ? <small>No policy versions</small> : <ul>{entry.versions.map((item) => <li key={item.id}>v{item.version_number} · {item.status} · {item.effective_from || "No start"} to {item.effective_to || "No end"} · {item.totalWeight.toFixed(2)}% · {item.complete ? "Complete template" : `${item.categoryCount}/8 categories or settings incomplete`} · Q4: {item.q4Applicable ? "applicable" : "not applicable"}</li>)}</ul>}</div>)}
              </>}
            </section>}
            <section><h2><History size={17} aria-hidden="true" /> Version history</h2>{versions.length === 0 ? <p>No versions for this designation.</p> : <ol>{versions.map((item) => <li key={item.id}><button type="button" className={item.id === version?.id ? "selected" : ""} disabled={busy} onClick={() => loadVersion(item.id)}><span>v{item.version_number} · {item.status}{item.status === "published" && item.effective_to ? " · historical" : ""}</span><small>{effectiveLabel(item)}</small><small>{item.published_by_name || item.created_by_name || "Admin"}</small></button></li>)}</ol>}</section>
            <section><h2><History size={17} aria-hidden="true" /> Audit history</h2>{audit.length === 0 ? <p>No audit events for this designation.</p> : <ol className="kpi-audit-list">{audit.map((event, index) => <li key={`${event.action}-${event.changed_at}-${index}`}><strong>v{event.version_number || "?"} · {event.action.replaceAll("_", " ")}</strong><span>{event.changed_by_name || "Former user"}</span><time dateTime={event.changed_at}>{new Date(event.changed_at).toLocaleString()}</time>{event.reason && <small>{event.reason}</small>}</li>)}</ol>}</section>
          </aside>
        </div>
      )}
    </main>
  );
}
